import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { cn } from '@/lib/utils'
import { RollText } from '@/components/generals/roll-link'
import { Magnetic } from '@/components/generals/magnetic'

interface ArrowLinkProps {
    href: string
    label: string
    className?: string
    labelClassName?: string
    external?: boolean
    tone?: 'dark' | 'light'
    /* The chevron is the accent by default, which disappears when the link
       sits on the accent panel itself. 'ink' and 'paper' are the escapes. */
    chevron?: 'brand' | 'ink' | 'paper'
}

const chevronStyles: Record<'brand' | 'ink' | 'paper', string> = {
    brand: 'bg-brand text-paper',
    ink: 'bg-ink text-paper',
    paper: 'bg-paper text-ink'
}

/* Label with a roll-up swap next to a square accent chevron. Used for every
   secondary CTA in the reference. */
export const ArrowLink: FC<ArrowLinkProps> = ({
    href,
    label,
    className,
    labelClassName,
    external = false,
    tone = 'dark',
    chevron = 'brand'
}): ReactNode => {
    const inner = (
        <>
            <RollText
                label={label}
                className={cn(
                    't-cond pb-[0.1em] text-[17px] leading-none',
                    tone === 'dark' ? 'text-ink' : 'text-paper',
                    labelClassName
                )}
            />

            <Magnetic strength={0.25}>
                <span
                    className={cn(
                        'flex size-9 items-center justify-center rounded-[var(--radius-chip)] transition-transform duration-500 group-hover/roll:rotate-45',
                        chevronStyles[chevron]
                    )}
                >
                    <span data-magnetic-inner className='block'>
                        <svg viewBox='0 0 24 24' className='size-3.5' fill='none' aria-hidden='true'>
                            <path d='M5 19L19 5M19 5H8M19 5v11' stroke='currentColor' strokeWidth='2' />
                        </svg>
                    </span>
                </span>
            </Magnetic>
        </>
    )

    const classes = cn('group/roll inline-flex items-center gap-4', className)

    if (external) {
        return (
            <a href={href} data-cursor='link' className={classes}>
                {inner}
            </a>
        )
    }

    return (
        <Link href={href} data-cursor='link' className={classes}>
            {inner}
        </Link>
    )
}
